import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { List, BarChart2, ArrowLeftIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import CategoryManagement from './CategoryManagement';
import QuestionManagement from './QuestionManagement';
import GlassPanel from '../../components/GlassPanel';
import Button from '../../components/Button';
import ThemeToggleButton from '../../components/ThemeToggleButton';
import { useTheme } from '../../context/ThemeContext';
import { getUserRole } from '../../utils/auth';

const AdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'categories' | 'questions'>('questions');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isMaintainer, setIsMaintainer] = useState(false);
  const { theme } = useTheme();

  // Check if user is admin or maintainer
  useEffect(() => {
    const { isAdmin: admin, isMaintainer: maintainer } = getUserRole();
    setIsAdmin(admin);
    setIsMaintainer(maintainer);
    
    // If user is not admin and tries to access categories tab, redirect to questions tab
    if (!admin && activeTab === 'categories') {
      setActiveTab('questions');
    }
  }, [activeTab]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`min-h-screen ${theme === 'dark' ? 'bg-gradient-to-b from-[#2D3436] to-[#0D0F1A]' : 'bg-gradient-to-b from-[#EBF4FF] to-[#E6E6FA]'}`}
    >
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="space-y-8">
          <div className="flex justify-between items-center">
            <Link to="/">
              <Button
                variant="secondary"
                className="flex items-center gap-2"
                icon={<ArrowLeftIcon className="w-4 h-4" />}
              >
                العودة إلى الصفحة الرئيسية
              </Button>
            </Link>
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-[#2D3748]'}`}>
                {isAdmin ? 'لوحة الإدارة' : isMaintainer ? 'لوحة إدارة الأسئلة' : 'لوحة التحكم'}
              </h1>
              <ThemeToggleButton variant="icon-only" />
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {/* Only show categories tab for admin users */}
            {isAdmin && (
              <Button
                variant={activeTab === 'categories' ? 'primary' : 'secondary'}
                onClick={() => setActiveTab('categories')}
                className="flex items-center gap-3"
                icon={<List className="w-5 h-5" />}
              >
                إدارة الفئات
              </Button>
            )}
            <Button
              variant={activeTab === 'questions' ? 'primary' : 'secondary'}
              onClick={() => setActiveTab('questions')}
              className="flex items-center gap-3"
              icon={<BarChart2 className="w-5 h-5" />}
            >
              إدارة الأسئلة
            </Button>
          </div>

          <div className="w-full">
            {activeTab === 'categories' && isAdmin ? (
              <CategoryManagement />
            ) : (
              <GlassPanel variant="elevated" className="p-6">
                <QuestionManagement />
              </GlassPanel>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AdminDashboard;